import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, AlertStatus } from "../types/alerts";
import { mockAlerts } from "../data/mockAlerts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Play, Pause, MoreVertical, Edit, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function AlertsListPage() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<Alert[]>(mockAlerts);

  const handleStatusToggle = (alertId: string, currentStatus: AlertStatus) => {
    setAlerts((prev) =>
      prev.map((alert) =>
        alert.id === alertId
          ? {
              ...alert,
              status: currentStatus === "ACTIVE" ? "PAUSED" : "ACTIVE",
              updatedAt: new Date().toISOString(),
            }
          : alert
      )
    );
  };

  const handleDelete = (alertId: string) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== alertId));
  };

  const getStatusBadge = (status: AlertStatus) => {
    const variants: Record<
      AlertStatus,
      { variant: "default" | "secondary" | "outline"; label: string }
    > = {
      ACTIVE: { variant: "default", label: "Active" },
      PAUSED: { variant: "secondary", label: "Paused" },
      INACTIVE: { variant: "outline", label: "Inactive" },
    };
    const { variant, label } = variants[status];
    return (
      <Badge variant={variant} className="text-xs">
        {label}
      </Badge>
    );
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    return (
      date.toLocaleDateString() +
      " " +
      date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };

  return (
    <div className="flex flex-1 flex-col gap-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Alerts & Policies</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your AI security alerts and monitoring policies
          </p>
        </div>
        <Button onClick={() => navigate("/alerts/new")}>
          <Plus className="h-4 w-4 mr-2" />
          Create Alert
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">Alerts ({alerts.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {alerts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>No alerts configured. Create your first alert to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Query</TableHead>
                  <TableHead>Frequency</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Triggered</TableHead>
                  <TableHead>Trigger Count</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {alerts.map((alert) => (
                  <TableRow
                    key={alert.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/alerts/${alert.id}/edit`)}
                  >
                    <TableCell className="font-medium">{alert.name}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          alert.priority === "P0"
                            ? "default"
                            : alert.priority === "P1"
                              ? "default"
                              : "secondary"
                        }
                        className={`text-xs ${
                          alert.priority === "P0"
                            ? "bg-red-500 hover:bg-red-600"
                            : alert.priority === "P1"
                              ? "bg-orange-500 hover:bg-orange-600"
                              : ""
                        }`}
                      >
                        {alert.priority}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-md">
                      <p className="text-sm text-muted-foreground truncate">{alert.query}</p>
                    </TableCell>
                    <TableCell className="text-sm">{alert.frequency}</TableCell>
                    <TableCell>{getStatusBadge(alert.status)}</TableCell>
                    <TableCell className="text-sm">{formatDate(alert.lastTriggered)}</TableCell>
                    <TableCell className="text-sm">{alert.triggerCount}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/alerts/${alert.id}/edit`)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleStatusToggle(alert.id, alert.status)}
                          >
                            {alert.status === "ACTIVE" ? (
                              <>
                                <Pause className="mr-2 h-4 w-4" />
                                Pause
                              </>
                            ) : (
                              <>
                                <Play className="mr-2 h-4 w-4" />
                                Activate
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleDelete(alert.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
